import type { WidgetConfig } from "./types";
import type { WidgetModule } from "./widget-module";
import { buildPresets, type WidgetPreset } from "./catalog";

/**
 * An isolated widget registry instance. Create one per consumer
 * ({@link createWidgetRegistry}) and provide it through {@link WidgetRegistryProvider};
 * the kit never owns a process-global. Unknown keys resolve to `undefined` so the
 * runtime renders a graceful placeholder instead of crashing on a removed widget.
 */
export type WidgetRegistry = {
  register<C extends WidgetConfig>(def: WidgetModule<C>): void;
  get(type: string): WidgetModule | undefined;
  list(): WidgetModule[];
  resolveConfig(
    def: WidgetModule | undefined,
    raw: WidgetConfig | null | undefined,
    fromVersion: number,
  ): WidgetConfig;
  /** Add-Card gallery presets, computed lazily + memoized off this instance. */
  getPresets(): WidgetPreset[];
};

export function createWidgetRegistry(): WidgetRegistry {
  const modules = new Map<string, WidgetModule<any>>();
  let presetsCache: WidgetPreset[] | null = null;

  function resolveConfig(
    def: WidgetModule | undefined,
    rawConfig: WidgetConfig | null | undefined,
    fromVersion: number,
  ): WidgetConfig {
    const raw = rawConfig ?? {};
    if (!def?.configSchema) return raw;
    const version = def.schemaVersion ?? 1;
    const migrated =
      def.migrate && fromVersion < version ? def.migrate(raw, fromVersion) : raw;
    const parsed = def.configSchema.safeParse(migrated);
    if (parsed.success) return parsed.data as WidgetConfig;
    // Fall back to schema defaults so a schema with a required field (no
    // `.default()`) keeps the Reset handler safe instead of crashing the editor.
    return schemaDefaults(def.configSchema);
  }

  return {
    register(def) {
      modules.set(def.type, def as WidgetModule<any>);
      presetsCache = null;
    },
    get(type) {
      return modules.get(type);
    },
    list() {
      return [...modules.values()];
    },
    resolveConfig,
    getPresets() {
      if (!presetsCache) presetsCache = buildPresets([...modules.values()]);
      return presetsCache;
    },
  };
}

/** `parse({})`-equivalent that never throws: returns the schema defaults, or `{}`
 *  when the schema has no full set of defaults (a missing `.default()`). */
function schemaDefaults(schema: NonNullable<WidgetModule["configSchema"]>): WidgetConfig {
  const parsed = schema.safeParse({});
  return (parsed.success ? parsed.data : {}) as WidgetConfig;
}
